import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/apiEndpoints';
import ModernTextField from '../components/ui/ModernTextField';
import { 
  ArrowLeft, PersonStanding, Mail, Phone, Calendar, 
  Home, BookOpen, Lock, BadgeIndianRupee, Car, 
  Camera, UploadCloud, XCircle
} from 'lucide-react';
import './Register.css';

// Exact match of your Kotlin password policy
const validatePasswordPolicy = (password) => ({
  "Minimum 8 characters": password.length >= 8,
  "At least 1 uppercase letter": /[A-Z]/.test(password),
  "At least 1 lowercase letter": /[a-z]/.test(password),
  "At least 1 number": /\d/.test(password),
  "At least 1 special character": /[^A-Za-z0-9]/.test(password),
  "No spaces allowed": !/\s/.test(password)
});

// Convert the profile picture to a Telegram-safe square before it is uploaded.
// The centre crop preserves the main subject for typical portrait photos.
const cropProfilePhotoToSquare = (file) => new Promise((resolve, reject) => {
  const sourceUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    const cropSize = Math.min(image.naturalWidth, image.naturalHeight);
    const sourceX = (image.naturalWidth - cropSize) / 2;
    const sourceY = (image.naturalHeight - cropSize) / 2;
    const outputSize = Math.min(cropSize, 1024);
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    canvas.getContext('2d').drawImage(
      image,
      sourceX,
      sourceY,
      cropSize,
      cropSize,
      0,
      0,
      outputSize,
      outputSize
    );
    URL.revokeObjectURL(sourceUrl);
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('Unable to crop the selected photo'));
      resolve(new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  };

  image.onerror = () => {
    URL.revokeObjectURL(sourceUrl);
    reject(new Error('Unable to read the selected photo'));
  };
  image.src = sourceUrl;
});

// Keep the complete Aadhaar card visible while limiting its dimensions to a
// range Telegram accepts for photo uploads.
const normalizeAadhaarPhoto = (file) => new Promise((resolve, reject) => {
  const sourceUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    const maxDimension = 1600;
    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(sourceUrl);
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('Unable to prepare Aadhaar photo'));
      resolve(new File([blob], 'aadhaar-photo.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  image.onerror = () => {
    URL.revokeObjectURL(sourceUrl);
    reject(new Error('Unable to read the selected Aadhaar photo'));
  };
  image.src = sourceUrl;
});

const Register = () => {
  const navigate = useNavigate();
  
  // Form State
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [exam, setExam] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [aadhaar, setAadhaar] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");

  // Files & Previews
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [aadhaarPreview, setAadhaarPreview] = useState(null);

  // Hidden File Inputs
  const photoInputRef = useRef(null);
  const aadhaarInputRef = useRef(null);

  // UI State
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Password rules logic
  const passwordRules = validatePasswordPolicy(password);
  const remainingRules = Object.entries(passwordRules).filter(([_, passed]) => !passed);
  const showRules = password.length > 0 && remainingRules.length > 0;

  // File Selection Handlers
  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const croppedPhoto = await cropProfilePhotoToSquare(file);
        if (photoPreview) URL.revokeObjectURL(photoPreview);
        setPhotoFile(croppedPhoto);
        setPhotoPreview(URL.createObjectURL(croppedPhoto));
        setErrorMsg("");
      } catch (error) {
        setPhotoFile(null);
        setPhotoPreview(null);
        setErrorMsg(error.message || "Unable to prepare profile photo");
      }
    }
  };

  const handleAadhaarSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const normalizedPhoto = await normalizeAadhaarPhoto(file);
        if (aadhaarPreview) URL.revokeObjectURL(aadhaarPreview);
        setAadhaarFile(normalizedPhoto);
        setAadhaarPreview(URL.createObjectURL(normalizedPhoto));
        setErrorMsg("");
      } catch (error) {
        setAadhaarFile(null);
        setAadhaarPreview(null);
        setErrorMsg(error.message || "Unable to prepare Aadhaar photo");
      }
    }
  };

  const handleSubmit = async () => {
    setErrorMsg("");

    // Validation exactly mirroring your Kotlin rules
    if (!photoFile) return setErrorMsg("Upload profile photo");
    if (!aadhaarFile) return setErrorMsg("Upload Aadhaar photo");
    if (password === "" || password !== confirmPassword) return setErrorMsg("Passwords do not match");
    if (!/^[A-Za-z\s]+$/.test(fullName)) return setErrorMsg("Invalid Name");
    if (phone.length !== 10) return setErrorMsg("Invalid Phone");
    if (aadhaar.length !== 12) return setErrorMsg("Invalid Aadhaar Number");
    if (remainingRules.length > 0) return setErrorMsg("Password does not meet requirements");

    let normalizedVehicle = null;
    if (vehicleNumber.trim() !== "") {
      normalizedVehicle = vehicleNumber.replace(/[\s\-\/]/g, "").toUpperCase();
      const vehicleRegex = /^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/;
      if (!vehicleRegex.test(normalizedVehicle)) {
        return setErrorMsg("Invalid Indian Vehicle Number (e.g. UP32AB1234)");
      }
    }

    setLoading(true);

    try {
      // Format DOB from YYYY-MM-DD (HTML native) to DD-MM-YYYY (Your API expectation)
      const formattedDob = dob ? dob.split('-').reverse().join('-') : "";

      const requestData = {
        fullName,
        email,
        phone,
        aadhaar,
        address,
        exam,
        dob: formattedDob,
        password,
        vehicleNumber: normalizedVehicle
      };

const formData = new FormData();

// Backend expects all form fields inside "data"
formData.append("data", JSON.stringify(requestData));

// Backend expects these exact file names
formData.append("photo", photoFile);
formData.append("aadhaarPhoto", aadhaarFile);

const response = await ApiService.registerWithPhoto(formData);

      if (response.status === 200 || response.status === 201) {
        alert("Account created successfully!");
        navigate('/app/signin'); // Navigate to login on success
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message || "Server error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-screen">
      {/* Top Bar */}
      <div className="top-bar">
        <button onClick={() => navigate(-1)} className="icon-button">
          <ArrowLeft size={24} color="#1e3a8a" />
        </button>
        <h2 className="top-bar-title">New Student</h2>
        <div style={{ width: 24 }}></div> {/* Spacer */}
      </div>

      <div className="scroll-content">
        {/* Profile Photo Uploader */}
        <div className="photo-upload-section">
          <input type="file" accept="image/*" ref={photoInputRef} onChange={handlePhotoSelect} hidden />
          <div className="profile-circle" onClick={() => photoInputRef.current?.click()}>
            {photoPreview ? (
              <img src={photoPreview} alt="Profile" className="preview-img profile-preview" />
            ) : (
              <PersonStanding size={60} color="var(--text-light)" />
            )}
            <div className="camera-badge">
              <Camera size={16} color="white" />
            </div>
          </div>
        </div>

        {/* Form Sections */}
        <div className="form-section">
          <h3 className="section-title">Personal Details</h3>
          
          <ModernTextField label="Full Name" value={fullName} onChange={setFullName} icon={PersonStanding} />
          <ModernTextField label="Email Address" value={email} onChange={setEmail} icon={Mail} type="email" />
          <ModernTextField label="Phone Number" value={phone} onChange={(v) => setPhone(v.replace(/\D/g, ''))} icon={Phone} type="tel" maxLength={10} />
          <ModernTextField label="Date of Birth" value={dob} onChange={setDob} icon={Calendar} type="date" />
          <ModernTextField label="Full Address" value={address} onChange={setAddress} icon={Home} />
          <ModernTextField label="Preparing For (Exam)" value={exam} onChange={setExam} icon={BookOpen} />
          
          <h3 className="section-title">Security</h3>
          <ModernTextField label="Password" value={password} onChange={setPassword} icon={Lock} type="password" />
          
          {showRules && (
            <div className="password-rules">
              {remainingRules.map(([rule]) => (
                <div key={rule} className="rule-item">
                  <XCircle size={14} color="var(--danger-red)" />
                  <span>{rule}</span>
                </div>
              ))}
            </div>
          )}

          <ModernTextField label="Confirm Password" value={confirmPassword} onChange={setConfirmPassword} icon={Lock} type="password" />

          <h3 className="section-title">Identity Verification</h3>
          <ModernTextField label="Aadhaar Number" value={aadhaar} onChange={(v) => setAadhaar(v.replace(/\D/g, ''))} icon={BadgeIndianRupee} type="tel" maxLength={12} />
          <ModernTextField label="Vehicle Number (optional)" value={vehicleNumber} onChange={setVehicleNumber} icon={Car} />

          {/* Aadhaar Upload Box */}
          <input type="file" accept="image/*" ref={aadhaarInputRef} onChange={handleAadhaarSelect} hidden />
          <div 
            className={`id-upload-box ${aadhaarPreview ? 'has-image' : ''}`}
            onClick={() => aadhaarInputRef.current?.click()}
          >
            {aadhaarPreview ? (
              <>
                <img src={aadhaarPreview} alt="Aadhaar" className="preview-img" />
                <div className="image-overlay">Tap to change</div>
              </>
            ) : (
              <div className="upload-placeholder">
                <UploadCloud size={40} color="var(--brand-blue)" />
                <span className="primary-text">Upload Aadhaar Photo</span>
                <span className="secondary-text">(Image only, No PDF)</span>
              </div>
            )}
          </div>
        </div>

        {/* Terms and Submit */}
        <div className="terms-row">
          <input 
            type="checkbox" 
            checked={acceptedDisclaimer}
            onChange={(e) => {
              if(e.target.checked) setShowDisclaimer(true);
              else setAcceptedDisclaimer(false);
            }} 
            id="terms-check" 
          />
          <label htmlFor="terms-check" onClick={(e) => { e.preventDefault(); setShowDisclaimer(true); }}>
            I agree to the terms and conditions.
          </label>
        </div>

        {errorMsg && <div className="error-message">{errorMsg}</div>}

        <button 
          className="submit-button" 
          onClick={handleSubmit} 
          disabled={!acceptedDisclaimer || loading}
        >
          {loading ? <div className="spinner"></div> : "Create Account"}
        </button>
      </div>

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Terms</h3>
            <p>Please note that the admission fee/deposit and the monthly fees are strictly non-refundable under any circumstances, and all rules must be followed.</p>
            <div className="modal-actions">
              <button className="submit-button" onClick={() => {
                setAcceptedDisclaimer(true);
                setShowDisclaimer(false);
              }}>I Agree</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
